import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { atomWithQuery } from 'jotai-tanstack-query';

import { AccessTokenAtom } from '@/client/jwt';
import { UserInfoResponse } from '@/app/api/user/info+api';
import { client } from '@/client';

const UserDataAtom = atomWithQuery((get) => ({
  queryKey: ['UserInfoQuery', get(AccessTokenAtom)],
  queryFn: () => {
    const accessToken = get(AccessTokenAtom);

    if (!accessToken) {
      return null;
    }

    return client.get<UserInfoResponse>('/user/info');
  },
  staleTime: 1000 * 60 * 60 * 24, // 24 hours
  gcTime: 1000 * 60 * 60 * 24 * 7, // 1 week
  refetchOnMount: false,
  refetchOnWindowFocus: false,
}));

export const useInvalidateUserData = () => {
  const [{ refetch: refetchUser }] = useAtom(UserDataAtom);
  const invalidateQueries = useCallback(() => {
    refetchUser();
  }, [refetchUser]);
  return invalidateQueries;
};

const IsLoadingUserDataAtom = atom((get) => {
  return get(UserDataAtom).isLoading;
});

export const useIsLoadingUserData = () => {
  return useAtomValue(IsLoadingUserDataAtom);
};

const UserAtom = atom((get) => {
  const userData = get(UserDataAtom).data?.data;
  if (!userData) {
    return null;
  }
  const plan = userData?.plan;
  const github = userData?.github;

  // isSupporter uses the same logic as the old isSubscribed
  // (active or cancelled status both count as supporters)
  const isSupporter = Boolean(
    plan?.status === 'active' || plan?.status === 'cancelled'
  );

  return {
    ...userData?.user,
    plan,
    isSupporter,
    github,
  };
});

export const useUser = () => {
  return useAtomValue(UserAtom);
};

export const useUserPlan = () => {
  const planData = useAtomValue(UserAtom)?.plan;
  const user = useAtomValue(UserAtom);

  const subscriptionType = useMemo(() => {
    if (!planData) {
      return null;
    }
    if (!user?.isSupporter) {
      return null;
    }
    return planData.subscriptionType;
  }, [planData, user]);

  return {
    planData,
    currentPlan: subscriptionType,
  };
};

export const useSignOut = () => {
  const setAccessToken = useSetAtom(AccessTokenAtom);

  const signOut = useCallback(() => {
    setAccessToken(null);
  }, [setAccessToken]);

  return signOut;
};
